import React from "react";

import { Box, Paper, Popper } from "@material-ui/core";
import type { PopperProps } from "@material-ui/core/Popper";
import { useField } from "formik";
import styled from "styled-components/macro";

import { EditButton } from "components/Button";
import { NVDWarningChips } from "components/Chip";
import { Autocomplete, Label } from "components/Form/FormikControls";
import { Column, Row } from "components/Layout";
import { BoldText, SmallText, Text } from "components/Text";

import {
  ConsignmentAutocompleteOption,
  useConsignmentAutocompleteOptions,
} from "./useConsignmentAutocompleteOptions";

const NVDWarningChipsComponent = NVDWarningChips as React.ComponentType<{
  warnings: string[];
}>;

const SelectedEditButton = EditButton as React.ComponentType<{
  dataTour?: string;
  disabled?: boolean;
  onClick?: () => void;
  size?: number;
}>;

const ConsignmentOptionPaper = styled(Paper)`
  color: black;
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
  padding: ${({ theme }) => theme.space[1]}px ${({ theme }) => theme.space[2]}px;
  width: 100%;
`;

const SelectedConsignment = styled(Box)<{ $disabled?: boolean }>`
  align-items: stretch;
  cursor: ${({ $disabled }) => ($disabled ? "default" : "pointer")};
  display: flex;
  flex-grow: 1;
  text-align: left;
  width: 100%;
`;

interface FilterOptionsParams {
  inputValue: string;
  [key: string]: unknown;
}

interface ConsignmentOptionCardProps {
  option: ConsignmentAutocompleteOption;
}

interface ConsignmentAutocompleteProps {
  disabled?: boolean;
  filterOptions?: (
    options: ConsignmentAutocompleteOption[],
    params: FilterOptionsParams,
  ) => ConsignmentAutocompleteOption[];
  label?: string;
  includeCreateUnknownConsignmentOptions?: boolean;
  includeSelectedConsignmentWarnings?: boolean;
  limitToCanAddSaleLotPermission?: boolean;
  menuPortalTarget?: Element | null;
  name: string;
  onChangeExtra?: (value: number | string | null) => void;
  onClose?: () => void;
  optionLabelMode?: "detailed" | "inline";
  options?: ConsignmentAutocompleteOption[];
  required?: boolean;
  selectedEditButtonDataTour?: string;
  selectedEditButtonSize?: number;
  selectedOption?: ConsignmentAutocompleteOption;
  showSelectedAsCard?: boolean;
  showAgencyName?: boolean;
  showConsignmentCode?: boolean;
  sortKnownConsignmentsFirst?: boolean;
  [key: string]: unknown;
}

const ConsignmentOptionCard = ({ children }: { children: React.ReactNode }) => (
  <ConsignmentOptionPaper variant="outlined" square={false}>
    {children}
  </ConsignmentOptionPaper>
);

const ConsignmentOptionCardComponent = ({
  option,
}: ConsignmentOptionCardProps) => {
  if (option.isConsignment === false) {
    return (
      <ConsignmentOptionCard>
        <BoldText>{option.label}</BoldText>
      </ConsignmentOptionCard>
    );
  }

  const vendorNumber = [option.livestockAgencyCode, option.vendorNumber]
    .filter(Boolean)
    .join("");
  const { nvd, pic, quantity, warnings = [] } = option;
  const vendorName = option.vendorName || option.label || "Unknown";

  return (
    <ConsignmentOptionCard>
      <Row flexWrap gridGap={1}>
        {vendorNumber && <Text color="gray40">{vendorNumber}</Text>}
        <BoldText>{vendorName}</BoldText>
      </Row>
      <SmallText justifyContent="flex-start">
        <Row flexWrap gridGap={1} fullWidth>
          {quantity ? <span>Hd: {quantity}</span> : null}
          {nvd ? <span>NVD: {nvd}</span> : null}
          {pic ? <span>PIC: {pic}</span> : null}
        </Row>
      </SmallText>
      {warnings.length > 0 && (
        <Row flexWrap marginTop={1}>
          <NVDWarningChipsComponent warnings={warnings} />
        </Row>
      )}
    </ConsignmentOptionCard>
  );
};

const renderConsignmentOption = (option: ConsignmentAutocompleteOption) => (
  <ConsignmentOptionCardComponent option={option} />
);

export const ConsignmentAutocomplete = ({
  disabled = false,
  filterOptions,
  label,
  includeCreateUnknownConsignmentOptions = false,
  includeSelectedConsignmentWarnings = false,
  limitToCanAddSaleLotPermission = false,
  menuPortalTarget,
  name,
  onChangeExtra,
  onClose,
  optionLabelMode = "detailed",
  options: providedOptions,
  required = false,
  selectedOption,
  showSelectedAsCard = false,
  showAgencyName = true,
  showConsignmentCode = true,
  sortKnownConsignmentsFirst = false,
  selectedEditButtonDataTour = "editConsignment",
  selectedEditButtonSize,
  ...props
}: ConsignmentAutocompleteProps) => {
  const [field, , helpers] = useField<number | string | null>(name);
  const generatedOptions = useConsignmentAutocompleteOptions({
    fieldValue: field.value,
    includeCreateUnknownConsignmentOptions,
    includeSelectedConsignmentWarnings,
    limitToCanAddSaleLotPermission,
    optionLabelMode,
    showAgencyName,
    showConsignmentCode,
    sortKnownConsignmentsFirst,
  });
  const options: ConsignmentAutocompleteOption[] =
    providedOptions || generatedOptions;

  const resolvedSelectedOption =
    selectedOption || options.find(option => option.value === field.value);
  const [isEditing, setIsEditing] = React.useState(
    !showSelectedAsCard || !resolvedSelectedOption,
  );
  const selectedOptionValue = resolvedSelectedOption?.value;
  const fieldValueRef = React.useRef(field.value);
  const originalValueRef = React.useRef(selectedOptionValue);

  React.useEffect(() => {
    fieldValueRef.current = field.value;
  }, [field.value]);

  React.useEffect(() => {
    if (
      showSelectedAsCard &&
      selectedOptionValue !== undefined &&
      selectedOptionValue !== null
    ) {
      originalValueRef.current = selectedOptionValue;
      setIsEditing(false);
    }
  }, [selectedOptionValue, showSelectedAsCard]);

  React.useEffect(() => {
    if (disabled && showSelectedAsCard && resolvedSelectedOption) {
      setIsEditing(false);
    }
  }, [disabled, resolvedSelectedOption, showSelectedAsCard]);

  const PopperComponent = menuPortalTarget
    ? (popperProps: PopperProps) => (
        <Popper {...popperProps} container={menuPortalTarget} />
      )
    : undefined;

  const startEditing = () => {
    originalValueRef.current = selectedOptionValue;
    setIsEditing(true);
  };

  const handleBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    if (!showSelectedAsCard) {
      return;
    }

    const nextFocusedElement = event.relatedTarget;
    if (
      nextFocusedElement instanceof Node &&
      event.currentTarget.contains(nextFocusedElement)
    ) {
      return;
    }

    window.setTimeout(() => {
      const originalValue = originalValueRef.current;
      if (
        fieldValueRef.current == null &&
        originalValue !== undefined &&
        originalValue !== null
      ) {
        helpers.setValue(originalValue);
        onChangeExtra?.(originalValue);
      }
      setIsEditing(false);
    }, 0);
  };

  // If not currently editing, render the card view of the option - it is not simple to
  // render the card view in the input for the select.  When editing, switch to the select/Autocomplete.
  if (showSelectedAsCard && resolvedSelectedOption && !isEditing) {
    return (
      <Column fullWidth fullHeight>
        <Row alignCenter justifyBetween>
          <Label htmlFor={name} required={!!label && required}>
            {label}
          </Label>
        </Row>
        <Row alignStretch flexGrow={1}>
          {!disabled && (
            <Row marginRight={1}>
              <SelectedEditButton
                dataTour={selectedEditButtonDataTour}
                onClick={startEditing}
                size={selectedEditButtonSize}
              />
            </Row>
          )}
          <SelectedConsignment
            aria-disabled={disabled}
            $disabled={disabled}
            onClick={() => {
              if (!disabled) {
                startEditing();
              }
            }}
          >
            {renderConsignmentOption(resolvedSelectedOption)}
          </SelectedConsignment>
        </Row>
      </Column>
    );
  }

  return (
    <Box onBlur={handleBlur}>
      <Autocomplete
        required={required}
        disabled={disabled}
        name={name}
        options={options}
        label={label}
        autoFocus={showSelectedAsCard}
        openOnFocus={showSelectedAsCard}
        onChangeExtra={value => {
          if (showSelectedAsCard && value) {
            setIsEditing(false);
          }
          onChangeExtra?.(value);
        }}
        onClose={() => {
          if (showSelectedAsCard && resolvedSelectedOption) {
            setIsEditing(false);
          }
          onClose?.();
        }}
        filterOptions={filterOptions}
        getOptionValue={option => option?.value}
        getOptionSelected={(option, selected) =>
          option?.value === selected?.value
        }
        renderOption={renderConsignmentOption}
        selectOnFocus
        handleHomeEndKeys
        PopperComponent={PopperComponent}
        disableAutoComplete
        {...props}
      />
    </Box>
  );
};
